import React, { useState } from 'react';
import { Link } from "react-router-dom";
import { Utils, getMarketingUrl } from '../../utils';
import './NewUserSignup.css';
import logo from "../../assets/notifysphere-logo.png";
import { IoAlertCircle } from "react-icons/io5";
import { FiClock, FiLock, FiMail } from "react-icons/fi";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import eyeOpenImg from "../../assets/icons/eye.png";
import eyeCloseImg from "../../assets/icons/invisible.png";
import web from '../../assets/icons/webimg.png';

function NewUserSignup() {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showCheckedAlert, setShowCheckedAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");

  // Form data state
  const [formData, setFormData] = useState({
    // Account Info
    email: '',
    password: '',
    // Personal Info
    firstName: '',
    lastName: '',
    category: '',
    phone: '',
    // Organization Info
    companyName: '',
    website: '',
    noWebsite: false,
    industry: '',
    companySize: '',
    // App Info
    appName: '',
    channel: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  const roleOptions = [
    { value: '', label: 'Select...' },
    { value: 'developer', label: 'Developer' },
    { value: 'product-manager', label: 'Product Manager' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'founder', label: 'Founder / CEO' },
    { value: 'other', label: 'Other' }
  ];

  const industryOptions = [
    { value: '', label: 'Select...' },
    { value: 'B2B / Technology', label: 'B2B / Technology' },
    { value: 'Ecommerce / Retail', label: 'Ecommerce / Retail' },
    { value: 'Education', label: 'Education' },
    { value: 'Entertainment', label: 'Entertainment' },
    { value: 'Food & Beverage', label: 'Food & Beverage' },
    { value: 'Gaming', label: 'Gaming' },
    { value: 'Media / News', label: 'Media / News' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Other', label: 'Other' }
  ];

  const companySizeOptions = [
    { value: '', label: 'Select...' },
    { value: '1 - 9', label: '1 - 9 employees' },
    { value: '10 - 25', label: '10 - 25 employees' },
    { value: '26 - 100', label: '26 - 100 employees' },
    { value: '101 - 250', label: '101 - 250 employees' },
    { value: '250+', label: '250+ employees' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleChannelSelect = (channel) => {
    setFormData(prev => ({ ...prev, channel }));
    if (errors.channel) {
      setErrors(prev => ({ ...prev, channel: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password should be at least 6 characters';
      }
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.category) {
        newErrors.category = 'Please select your role';
      }
    }

    if (step === 2) {
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      if (!formData.website.trim() && !formData.noWebsite) {
        newErrors.website = 'Website is required or check "I don\'t have a website"';
      }
      if (!formData.industry) {
        newErrors.industry = 'Please select your industry';
      }
      if (!formData.companySize) {
        newErrors.companySize = 'Please select company size';
      }
    }

    if (step === 3) {
      if (!formData.appName.trim()) {
        newErrors.appName = 'App name is required';
      }
      if (!formData.channel) {
        newErrors.channel = 'Please select a channel';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && !isChecked) {
      setShowCheckedAlert(true);
      return;
    }
    setShowCheckedAlert(false);

    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(3)) {
      return;
    }

    setIsLoading(true);
    setFormError("");

    try {
      const response = await fetch(Utils.baseurl + 'user/register-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          category: formData.category,
          phone: formData.phone,
          companyName: formData.companyName.trim(),
          website: formData.noWebsite ? '' : formData.website.trim(),
          hasWebsite: !formData.noWebsite,
          industry: formData.industry,
          companySize: formData.companySize,
          appName: formData.appName.trim(),
          channel: formData.channel
        }),
      });

      const result = await response.json();
      setIsLoading(false);

      if (result.status) {
        setIsSubmitted(true);
      } else {
        if (result.errors) {
          const newErrors = {};
          result.errors.forEach(item => {
            newErrors[item.path] = item.message;
          });
          setErrors(newErrors);
          // Go back to the step with the error
          if (newErrors.email || newErrors.password || newErrors.firstName || newErrors.lastName || newErrors.category) {
            setCurrentStep(1);
          } else if (newErrors.companyName || newErrors.website || newErrors.industry || newErrors.companySize) {
            setCurrentStep(2);
          }
        } else {
          setFormError(result.message || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setFormError('Network error. Please try again.');
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Thank you page after submission
  if (isSubmitted) {
    return (
      <div className="new-signup-page">
        <div className="new-signup-page-inner">
          <div className="page_logo">
            <a href="/">
              <img src={logo} alt="notifySphere" />
            </a>
          </div>
          <div className="thank-you-container">
            <div className="thank-you-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <h1>Thank You for Registering!</h1>
            <p className="thank-you-message">
              Your registration request has been submitted successfully.
            </p>
            <p className="thank-you-submessage">
              Our team will review your application and contact you shortly.
              Once approved, you'll receive an email with instructions to log in
              using two-factor authentication.
            </p>
            <div className="thank-you-info">
              <div className="info-item">
                <span className="info-icon"><FiMail size={18} /></span>
                <span>Check your email for updates</span>
              </div>
              <div className="info-item">
                <span className="info-icon"><FiClock size={18} /></span>
                <span>Review typically takes 1-2 business days</span>
              </div>
              <div className="info-item">
                <span className="info-icon"><FiLock size={18} /></span>
                <span>Secure login with email 2FA</span>
              </div>
            </div>
            <Link to="/signIn" className="back-to-login-btn">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-signup-page">
      <div className="new-signup-page-inner">
        <div className="page_logo">
          <a href="/">
            <img src={logo} alt="notifySphere" />
          </a>
        </div>

        <div className="new-signup-container">
          <div className="signup-header">
            <h1>Create Your Account</h1>
            <p>Join notifySphere and start engaging your users</p>
          </div>

          {/* Progress Steps */}
          <div className="progress-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
              <div className="step-number">{currentStep > 1 ? <FontAwesomeIcon icon={faCheck} /> : '1'}</div>
              <span>Account</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
              <div className="step-number">{currentStep > 2 ? <FontAwesomeIcon icon={faCheck} /> : '2'}</div>
              <span>Organization</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <span>App Setup</span>
            </div>
          </div>

          {showCheckedAlert && (
            <div className='signup_alert_main'>
              <div className='signup_alert1'>
                <IoAlertCircle style={{ fontSize: "20px" }} />
              </div>
              <div className='signup_alert2'>
                <span>You must accept the Terms of Service before proceeding.</span>
              </div>
            </div>
          )}

          {formError && (
            <div className='alert alert-danger'>
              <span>{formError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Account & Personal Info */}
            {currentStep === 1 && (
              <div className="form-step">
                <h2>Tell us about you</h2>
                <p className="step-description">Please provide your account and personal details</p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address <span className="required">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Password <span className="required">*</span></label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Minimum 6 characters"
                        className={errors.password ? 'error' : ''}
                      />
                      <img
                        src={showPassword ? eyeCloseImg : eyeOpenImg}
                        alt="Toggle Password"
                        onClick={toggleShowPassword}
                        className="password-toggle"
                      />
                    </div>
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>
                </div>

                <div className="form-row two-columns">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name <span className="required">*</span></label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="First Name"
                      className={errors.firstName ? 'error' : ''}
                    />
                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Last Name"
                      className={errors.lastName ? 'error' : ''}
                    />
                    {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="category">Which category best describes your role? <span className="required">*</span></label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={errors.category ? 'error' : ''}
                    >
                      {roleOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.category && <span className="error-text">{errors.category}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number (optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+91 99999-99999"
                    />
                  </div>
                </div>

                <div className="terms-checkbox">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={isChecked}
                    onChange={(e) => setIsChecked(e.target.checked)}
                  />
                  <label htmlFor="terms">
                    I agree to notifySphere's{' '}
                    <a href={getMarketingUrl('/terms')} target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
                    <a href={getMarketingUrl('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Organization Info */}
            {currentStep === 2 && (
              <div className="form-step">
                <h2>Create your Organization</h2>
                <p className="step-description">Tell us about your company or organization</p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyName">Company or Organization Name <span className="required">*</span></label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      className={errors.companyName ? 'error' : ''}
                    />
                    {errors.companyName && <span className="error-text">{errors.companyName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="website">Website <span className="required">*</span></label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="site.com or www.site.com"
                      disabled={formData.noWebsite}
                      className={errors.website ? 'error' : ''}
                    />
                    {errors.website && <span className="error-text">{errors.website}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="noWebsite"
                      name="noWebsite"
                      checked={formData.noWebsite}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="noWebsite">I don't have a website</label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="industry">What industry best describes your company? <span className="required">*</span></label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                      className={errors.industry ? 'error' : ''}
                    >
                      {industryOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.industry && <span className="error-text">{errors.industry}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companySize">Company Size <span className="required">*</span></label>
                    <select
                      id="companySize"
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleInputChange}
                      className={errors.companySize ? 'error' : ''}
                    >
                      {companySizeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.companySize && <span className="error-text">{errors.companySize}</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: App Setup */}
            {currentStep === 3 && (
              <div className="form-step">
                <h2>Set up your first App</h2>
                <p className="step-description">Configure your first app or website</p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="appName">Name of your app/website <span className="required">*</span></label>
                    <input
                      type="text"
                      id="appName"
                      name="appName"
                      value={formData.appName}
                      onChange={handleInputChange}
                      placeholder="App Name"
                      className={errors.appName ? 'error' : ''}
                    />
                    {errors.appName && <span className="error-text">{errors.appName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Set up your first channel <span className="required">*</span></label>
                    <div className="channel-options">
                      <div
                        className={`channel-option ${formData.channel === 'apple-ios' ? 'selected' : ''}`}
                        onClick={() => handleChannelSelect('apple-ios')}
                      >
                        <svg style={{ height: "40px", width: "40px" }} viewBox="0 0 48 48" fill="currentColor">
                          <g fill="#000">
                            <path d="M33.051.057s-3.56.087-6.825 3.351c-3.266 3.265-2.715 7.663-2.715 7.663s3.232.592 6.682-2.858S33.05.057 33.05.057zM23.885 13.856c-2.407-.017-3.886-2.095-7.984-2.095-3.688 0-7.385 2.38-8.957 4.682-1.565 2.292-2.92 4.805-2.92 9.906 0 5.1 2.457 13.872 8.495 19.81 1.034 1.018 2.403 1.782 3.966 1.83 2.809.084 4.534-2.018 8.082-2.018 3.548 0 4.616 2.017 7.819 2.017 1.457 0 3.117-.663 4.73-2.38 2.07-2.2 4.64-6.393 5.947-10.414 0 0-6.551-2.53-6.473-9.824.066-6.045 5.2-9.018 5.2-9.018s-2.999-4.78-9.372-4.78c-4.401.033-6.373 2.299-8.533 2.284z"></path>
                          </g>
                        </svg>
                        <span>Apple iOS (FCM)</span>
                      </div>
                      <div
                        className={`channel-option ${formData.channel === 'google-android' ? 'selected' : ''}`}
                        onClick={() => handleChannelSelect('google-android')}
                      >
                        <svg style={{ height: "40px", width: "40px" }} viewBox="0 0 1274.37 718" fill="#3ddc84">
                          <path d="m930.77 536.42a53.07 53.07 0 1 1 53.06-53.08 53.14 53.14 0 0 1 -53.06 53.08m-586.54 0a53.07 53.07 0 1 1 53.06-53.08 53.13 53.13 0 0 1 -53.06 53.08m605.57-319.65 106.05-183.68a22.06 22.06 0 1 0 -38.21-22.09l-107.39 186c-82.12-37.48-174.35-58.35-272.76-58.35s-190.63 20.9-272.75 58.35l-107.38-186a22.06 22.06 0 1 0 -38.22 22.06l106.06 183.71c-182.11 99.05-306.67 283.41-324.89 501.23h1274.38c-18.24-217.82-142.79-402.18-324.89-501.23"></path>
                        </svg>
                        <span>Google Android (FCM)</span>
                      </div>
                      <div
                        className={`channel-option ${formData.channel === 'web' ? 'selected' : ''}`}
                        onClick={() => handleChannelSelect('web')}
                      >
                        <img src={web} width="40px" alt="Web" />
                        <span>Web</span>
                      </div>
                    </div>
                    {errors.channel && <span className="error-text">{errors.channel}</span>}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="form-navigation">
              {currentStep > 1 && (
                <button type="button" className="btn-back" onClick={handleBack}>
                  Back
                </button>
              )}
              {currentStep < 3 ? (
                <button type="button" className="btn-next" onClick={handleNext}>
                  Continue
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    'Submit Registration'
                  )}
                </button>
              )}
            </div>
          </form>

          <div className="already_acc">
            <span>Already have an account? <Link to="/signIn">Sign in</Link></span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewUserSignup;
